import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { cn } from "@/lib/utils";
import {
  type WorkspaceUsageResult,
  getUsagePercentage,
  getUsageRemaining,
  isUsageNearLimit,
  isUsageOverLimit,
} from "@/hooks/useWorkspaceUsage";
import { Bot, Globe, Instagram, MessageSquare, Users } from "lucide-react";
import type { ReactNode } from "react";

type MeterKind = "monthly" | "resource" | "connected";

interface UsageMeterProps {
  icon: ReactNode;
  label: string;
  used: number;
  limit: number;
  kind: MeterKind;
  formatNumbers?: boolean;
  overLimitDetail?: ReactNode;
}

function formatValue(value: number, formatNumbers?: boolean): string {
  return formatNumbers ? value.toLocaleString("pt-BR") : String(value);
}

function UsageMeter({
  icon,
  label,
  used,
  limit,
  kind,
  formatNumbers,
  overLimitDetail,
}: UsageMeterProps) {
  const percentage = getUsagePercentage(used, limit);
  const remaining = getUsageRemaining(used, limit);
  const overLimit = isUsageOverLimit(used, limit);
  const nearLimit = isUsageNearLimit(used, limit);
  const excess = overLimit ? used - limit : 0;

  const usedLabel =
    kind === "connected"
      ? used === 1
        ? "conectada"
        : "conectadas"
      : kind === "monthly"
        ? used === 1
          ? "usada"
          : "usadas"
        : used === 1
          ? "em uso"
          : "em uso";

  const remainingLabel = (() => {
    if (overLimit) {
      return `${formatValue(excess, formatNumbers)} acima do limite`;
    }
    if (remaining === 0) {
      return "Limite atingido";
    }
    if (kind === "connected") {
      return `${formatValue(remaining, formatNumbers)} ${
        remaining === 1 ? "vaga disponível" : "vagas disponíveis"
      } no plano`;
    }
    if (kind === "monthly") {
      return `${formatValue(remaining, formatNumbers)} restantes de ${formatValue(limit, formatNumbers)} no mês`;
    }
    return `${formatValue(remaining, formatNumbers)} restantes de ${formatValue(limit, formatNumbers)}`;
  })();

  return (
    <div
      className={cn(
        "rounded-lg border bg-background/60 p-3 space-y-2",
        overLimit && "border-destructive/40 bg-destructive/5",
        !overLimit && nearLimit && "border-amber-500/35 bg-amber-500/5",
      )}
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2 min-w-0">
          <span className="text-primary shrink-0">{icon}</span>
          <div className="min-w-0">
            <p className="text-sm font-medium leading-tight">{label}</p>
            <p className="text-[11px] text-muted-foreground leading-tight mt-0.5">
              {kind === "connected"
                ? "Integrações ativas no workspace"
                : kind === "monthly"
                  ? "Consumo no período atual"
                  : "Total no workspace"}
            </p>
          </div>
        </div>
        <Badge
          variant="secondary"
          className={cn(
            "shrink-0 tabular-nums text-[11px] font-medium",
            overLimit && "bg-destructive/15 text-destructive border-destructive/30",
            !overLimit && nearLimit && "bg-amber-500/15 text-amber-700 dark:text-amber-400 border-amber-500/30",
          )}
        >
          {formatValue(used, formatNumbers)} / {formatValue(limit, formatNumbers)} {usedLabel}
        </Badge>
      </div>

      <Progress
        value={percentage}
        className={cn(
          "h-1.5",
          overLimit && "[&>div]:bg-destructive",
          !overLimit && nearLimit && "[&>div]:bg-amber-500",
        )}
      />

      <div className="flex items-center justify-between gap-2 text-[11px]">
        <p
          className={cn(
            "text-muted-foreground",
            overLimit && "text-destructive font-medium",
            !overLimit && nearLimit && "text-amber-700 dark:text-amber-400 font-medium",
          )}
        >
          {nearLimit && !overLimit ? "Quase no limite · " : ""}
          {remainingLabel}
        </p>
        <span className="text-muted-foreground tabular-nums shrink-0">{percentage}%</span>
      </div>

      {overLimitDetail}
    </div>
  );
}

function WhatsAppIcon() {
  return (
    <svg className="w-4 h-4" viewBox="0 0 24 24" fill="currentColor" aria-hidden>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413Z" />
    </svg>
  );
}

function UsageGroup({ title, description, children }: { title: string; description: string; children: ReactNode }) {
  return (
    <div className="space-y-2">
      <div>
        <p className="text-xs font-semibold uppercase tracking-wide text-muted-foreground">{title}</p>
        <p className="text-[11px] text-muted-foreground">{description}</p>
      </div>
      <div className="space-y-2">{children}</div>
    </div>
  );
}

interface PlanUsageSectionProps {
  usageData: WorkspaceUsageResult;
  planDisplayName: string;
}

export function PlanUsageSection({ usageData, planDisplayName }: PlanUsageSectionProps) {
  const { usage, limits } = usageData;
  const scrapesUsed = usage.scrapes ?? 0;
  const scrapesLimit = limits.scrapes ?? 2;
  const llmOverLimit = isUsageOverLimit(usage.llm_outputs, limits.llm_outputs);
  const llmExcess = usage.llm_outputs - limits.llm_outputs;

  return (
    <div className="p-3 sm:p-4 bg-muted/50 rounded-lg space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-2 pb-3 border-b">
        <div>
          <p className="text-sm font-medium">Plano {planDisplayName}</p>
          <p className="text-xs text-muted-foreground">
            Período: {new Date(usageData.period_start).toLocaleDateString("pt-BR")} –{" "}
            {new Date(usageData.period_end).toLocaleDateString("pt-BR")}
          </p>
        </div>
        <p className="text-[11px] text-muted-foreground sm:text-right max-w-xs">
          Uso atual vs. limites incluídos no plano. Canais mostram apenas integrações conectadas.
        </p>
      </div>

      <UsageGroup title="Consumo mensal" description="Recursos que renovam a cada período de faturamento.">
        <UsageMeter
          icon={<MessageSquare className="w-4 h-4" />}
          label="Mensagens de IA"
          used={usage.llm_outputs}
          limit={limits.llm_outputs}
          kind="monthly"
          formatNumbers
          overLimitDetail={
            llmOverLimit ? (
              <p className="text-[11px] text-destructive">
                Excedente: {llmExcess.toLocaleString("pt-BR")} mensagens · R${" "}
                {(llmExcess * limits.extra_message_price).toFixed(2)} adicional
              </p>
            ) : undefined
          }
        />
        <UsageMeter
          icon={<Globe className="w-4 h-4" />}
          label="Importações de URL"
          used={scrapesUsed}
          limit={scrapesLimit}
          kind="monthly"
        />
      </UsageGroup>

      <UsageGroup title="Recursos do workspace" description="Capacidade total permitida no plano.">
        <UsageMeter
          icon={<Bot className="w-4 h-4" />}
          label="Agentes de IA"
          used={usage.agents}
          limit={limits.agents}
          kind="resource"
        />
        <UsageMeter
          icon={<Users className="w-4 h-4" />}
          label="Leads"
          used={usage.leads}
          limit={limits.leads}
          kind="resource"
          formatNumbers
        />
      </UsageGroup>

      <UsageGroup title="Canais conectados" description="Instâncias ativas — não é consumo mensal.">
        <UsageMeter
          icon={<WhatsAppIcon />}
          label="WhatsApp"
          used={usage.whatsapp}
          limit={limits.whatsapp}
          kind="connected"
        />
        <UsageMeter
          icon={<Instagram className="w-4 h-4" />}
          label="Instagram"
          used={usage.instagram}
          limit={limits.instagram}
          kind="connected"
        />
      </UsageGroup>
    </div>
  );
}
